import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { 
  LayoutDashboard, 
  Users, 
  HelpCircle, 
  GraduationCap, 
  IndianRupee, 
  UserCheck, 
  MessageCircle, 
  PhoneCall, 
  Sparkles 
} from 'lucide-react';
import { leadsService } from '../../services/leadsService';
import { enquiryService } from '../../services/enquiryService';

interface NavItem {
  name: string;
  href: string;
  icon: React.ElementType;
  badge?: string | number;
  badgeColor?: string;
}

export function CrmSidebar({ isCollapsed = false }: { isCollapsed?: boolean }) {
  const location = useLocation();
  const [leadCount, setLeadCount] = useState<number>(0);
  const [enquiryCount, setEnquiryCount] = useState<number>(0);

  useEffect(() => {
    async function loadCounts() {
      try {
        const leads = await leadsService.listLeads();
        const activeLeads = leads.filter(l => l.status !== 'dropped' && l.status !== 'enrolled').length;
        setLeadCount(activeLeads);

        const enquiries = await enquiryService.listEnquiries();
        const activeEnquiries = enquiries.enquiries?.filter(e => e.status === 'new' || e.status === 'contacted').length || 0;
        setEnquiryCount(activeEnquiries);
      } catch (err) {
        // Fallback gracefully
      }
    }
    loadCounts();
  }, []);

  const navItems: NavItem[] = [
    { 
      name: 'Executive Dashboard', 
      href: '/dashboard', 
      icon: LayoutDashboard 
    },
    { 
      name: 'Leads Pipeline', 
      href: '/leads', 
      icon: Users,
      badge: leadCount > 0 ? leadCount : undefined,
      badgeColor: 'bg-indigo-600 text-white'
    },
    { 
      name: 'Enquiries Desk', 
      href: '/enquiries', 
      icon: HelpCircle,
      badge: enquiryCount > 0 ? enquiryCount : undefined,
      badgeColor: 'bg-amber-500 text-white'
    },
    { 
      name: 'Student Admissions', 
      href: '/admissions', 
      icon: GraduationCap 
    },
    { 
      name: 'Fees & Receipts', 
      href: '/fees', 
      icon: IndianRupee 
    },
    { 
      name: 'Sales & Counselors', 
      href: '/team', 
      icon: UserCheck 
    },
    { 
      name: 'WhatsApp & Ad Tools', 
      href: '/campaign-tools', 
      icon: MessageCircle,
      badge: 'Live',
      badgeColor: 'bg-emerald-500 text-white'
    },
  ];

  return (
    <aside className={cn(
      "bg-slate-900 text-slate-200 border-r border-slate-800 h-[calc(100vh-4rem)] flex flex-col justify-between transition-all duration-300 ease-in-out shadow-xl select-none",
      isCollapsed ? "w-18" : "w-68"
    )}>
      {/* Top Nav List */}
      <div className="p-3.5 space-y-1.5 overflow-y-auto">
        {!isCollapsed && (
          <div className="px-3 py-2 mb-2 flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
              Sales & Admissions
            </span>
            <span className="text-[10px] font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-2 py-0.5 rounded-full flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              AWS CRM
            </span>
          </div>
        )}

        <nav className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href || 
                             (item.href !== '/dashboard' && location.pathname.startsWith(item.href));

            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center rounded-xl transition-all duration-200 group relative',
                  isCollapsed ? 'justify-center px-2 py-3' : 'space-x-3 px-3.5 py-2.5',
                  isActive
                    ? 'bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-semibold shadow-md shadow-indigo-900/30'
                    : 'text-slate-400 hover:bg-slate-800/80 hover:text-slate-100 font-medium'
                )}
                title={isCollapsed ? item.name : ''}
              >
                <Icon className={cn(
                  "w-5 h-5 transition-transform duration-200 group-hover:scale-110", 
                  isActive ? "text-white" : "text-slate-400 group-hover:text-indigo-400"
                )} />

                {!isCollapsed && (
                  <>
                    <span className="text-sm font-medium tracking-wide flex-1">{item.name}</span>
                    {item.badge && (
                      <span className={cn(
                        "text-[10px] font-bold px-2 py-0.5 rounded-full",
                        item.badgeColor || "bg-slate-700 text-slate-200"
                      )}>
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Bottom Hotline / Profile Card */}
      <div className="p-3 border-t border-slate-800 bg-slate-950/60">
        {!isCollapsed ? (
          <div className="bg-slate-900/90 border border-slate-800 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <PhoneCall className="w-3.5 h-3.5 text-emerald-400" />
                Admissions Desk
              </span>
              <span className="text-[10px] text-emerald-400 font-mono font-bold bg-emerald-950/80 border border-emerald-800/60 px-1.5 py-0.5 rounded">
                +91 9100073006
              </span>
            </div>
            <p className="text-[11px] text-slate-400 leading-tight">
              Inbound WhatsApp & Meta Ads leads automatically capture to CRM.
            </p>
            <Link
              to="/campaign-tools"
              className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-indigo-300 bg-indigo-950/70 hover:bg-indigo-900/80 border border-indigo-800/50 rounded-lg transition-colors"
            >
              <Sparkles className="w-3 h-3 text-indigo-400" />
              View Marketing Links
            </Link>
          </div>
        ) : (
          <div className="flex justify-center">
            <Link to="/campaign-tools" title="WhatsApp Admissions Desk">
              <MessageCircle className="w-5 h-5 text-emerald-400 hover:scale-110 transition-transform" />
            </Link>
          </div>
        )}
      </div>
    </aside>
  );
}
